import { Thumb } from "../lib/thumb";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useDateRange } from "@/lib/dateRange";
import { useChannel, channelParam } from "@/lib/channel";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import { Button } from "@/components/ui/button";
import ProductFormDialog from "@/components/ProductFormDialog";
import { fmtNumber } from "@/lib/format";
import { useCurrency } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ArrowUpDown, Plus, Pencil, Trash2, AlertTriangle, X, CheckCheck } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const { range } = useDateRange();
  const { channel } = useChannel();
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("revenue");
  const [showMissingCost, setShowMissingCost] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Multi-select
  const [selected, setSelected] = useState(new Set());

  // Single-delete confirmation
  const [confirmTarget, setConfirmTarget] = useState(null); // product to confirm-delete
  // Bulk-delete confirmation
  const [confirmBulk, setConfirmBulk] = useState(false);

  // Inline cost-setting mode
  const [setCostMode, setSetCostMode] = useState(false);
  // Map of productId -> draft cost string while in set-cost mode
  const [draftCosts, setDraftCosts] = useState({});
  // Track which row is currently being saved
  const [savingId, setSavingId] = useState(null);

  const { fmtMoney } = useCurrency();

  const loadProducts = useCallback(() =>
    api.get("/products").then((r) => setProducts(r.data || [])), []);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => {
    const params = { start: range.start, end: range.end, sort, limit: 200, channel: channelParam(channel) };
    api.get("/analytics/top-products", { params }).then((r) => setAnalytics(r.data || []));
  }, [range.start, range.end, sort, channel]);

  const merged = useMemo(() => {
    const bySku = Object.fromEntries(products.map((p) => [p.sku, p]));
    const analyticsBySku = Object.fromEntries(analytics.map((a) => [a.sku, a]));
    const skus = new Set([...products.map((p) => p.sku), ...analytics.map((a) => a.sku)]);
    const rows = Array.from(skus).map((sku) => {
      const p = bySku[sku] || {};
      const a = analyticsBySku[sku] || { units: 0, revenue: 0, profit: 0, margin: 0 };
      // COGS = unit cost from the product catalog
      const cogs = p.cost || 0;
      const price = p.price || 0;
      // Margin always derived from catalog price/cost so edits reflect immediately
      const margin = price > 0 ? +((price - cogs) / price * 100).toFixed(1) : 0;
      // Profit: order-based when orders exist, otherwise per-unit from catalog
      const profit = a.revenue > 0 ? a.profit : 0;
      const missingCost = price > 0 && cogs === 0 && typeof p.id === "string" && p.id.startsWith("woo_");
      return { ...p, sku, name: p.name || a.name, ...a, cogs, price, margin, profit, missingCost };
    });
    const filtered = rows.filter((r) => {
      // In set-cost mode, always show only missing-cost rows (ignore other filters)
      if (setCostMode) return r.missingCost;
      if (showMissingCost && !r.missingCost) return false;
      if (q && !(r.name?.toLowerCase().includes(q.toLowerCase()) || r.sku?.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
    return filtered.sort((x, y) => (y[sort] || 0) - (x[sort] || 0));
  }, [products, analytics, q, sort, showMissingCost, setCostMode]);

  // Count WooCommerce-synced products with price > 0 but no cost (before any active filter)
  const missingCostTotal = useMemo(
    () => products.filter((p) => typeof p.id === "string" && p.id.startsWith("woo_") && p.price > 0 && (!p.cost || p.cost === 0)).length,
    [products]
  );

  // IDs of rows that can actually be selected (have a real product record)
  const selectableIds = useMemo(() => merged.filter((r) => r.id).map((r) => r.id), [merged]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  // Single delete — triggered after AlertDialog confirms
  const doDelete = async (p) => {
    await api.delete(`/products/${p.id}`);
    toast.success(`"${p.name}" deleted`);
    setSelected((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
    loadProducts();
  };

  // Bulk delete — triggered after AlertDialog confirms
  const doBulkDelete = async () => {
    const ids = [...selected];
    await Promise.all(ids.map((id) => api.delete(`/products/${id}`)));
    toast.success(`${ids.length} product${ids.length > 1 ? "s" : ""} deleted`);
    setSelected(new Set());
    loadProducts();
  };

  // Bulk edit — open the form with shared fields pre-filled (editing an array signals bulk mode)
  const openBulkEdit = () => {
    const targets = merged.filter((r) => r.id && selected.has(r.id));
    setEditing({ _bulk: true, _targets: targets });
    setDialogOpen(true);
  };

  // ── Inline set-cost mode ──────────────────────────────────────────────────

  const enterSetCostMode = () => {
    // Pre-populate drafts as empty strings so each input starts blank
    const drafts = {};
    products
      .filter((p) => typeof p.id === "string" && p.id.startsWith("woo_") && p.price > 0 && (!p.cost || p.cost === 0))
      .forEach((p) => { drafts[p.id] = ""; });
    setDraftCosts(drafts);
    setSetCostMode(true);
  };

  const exitSetCostMode = () => {
    setSetCostMode(false);
    setDraftCosts({});
    loadProducts();
  };

  // Save a single product's cost. Returns true on success.
  const saveCost = useCallback(async (product, costStr) => {
    const cost = parseFloat(costStr);
    if (!costStr || isNaN(cost) || cost <= 0) return false; // skip blank / zero
    setSavingId(product.id);
    try {
      const payload = { ...product, cost };
      await api.put(`/products/${product.id}`, { ...payload, id: product.id });
      // Optimistically update local state so the row disappears
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, cost } : p))
      );
      setDraftCosts((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
      return true;
    } catch {
      toast.error(`Failed to save cost for "${product.name}"`);
      return false;
    } finally {
      setSavingId(null);
    }
  }, []);

  // Focus the next missing-cost input after the current one
  const focusNext = (currentId) => {
    // Find the rows still in the table (missingCost) and focus the one after currentId
    const inputEls = document.querySelectorAll("[data-cost-input]");
    const arr = Array.from(inputEls);
    const idx = arr.findIndex((el) => el.dataset.costInput === currentId);
    if (idx >= 0 && idx < arr.length - 1) {
      arr[idx + 1].focus();
    }
  };

  const handleCostKeyDown = async (e, product) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const saved = await saveCost(product, draftCosts[product.id] ?? "");
      if (saved) {
        // Move to next input
        focusNext(product.id);
      }
    } else if (e.key === "Tab") {
      // Allow default tab but save if there's a value
      const val = draftCosts[product.id] ?? "";
      if (val) {
        e.preventDefault();
        const saved = await saveCost(product, val);
        if (saved) {
          focusNext(product.id);
        }
      }
    } else if (e.key === "Escape") {
      exitSetCostMode();
    }
  };

  const handleCostBlur = async (product) => {
    const val = draftCosts[product.id] ?? "";
    if (val) {
      await saveCost(product, val);
    }
  };

  return (
    <div data-testid="products-page" className="max-w-[1400px] mx-auto">
      <PageHeader
         title="Retail products"
         subtitle={`${products.length} SKU${products.length !== 1 ? "s" : ""} · retail sales analytics in selected range`}
        testid="products-header"
        action={
          <Button data-testid="add-product-btn" onClick={openCreate}
            className="rounded-full bg-black text-white hover:bg-black/85">
             <Plus className="w-4 h-4 mr-1.5" /> Add retail product
          </Button>
        }
      />

      <Card>
        {/* Missing cost banner */}
        {missingCostTotal > 0 && !setCostMode && (
          <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-2xl bg-[#FF9500]/8 border border-[#FF9500]/30 text-[12.5px]">
            <AlertTriangle className="w-4 h-4 text-[#FF9500] flex-shrink-0" />
            <span className="flex-1">
              <strong>{missingCostTotal}</strong> product{missingCostTotal !== 1 ? "s" : ""} {missingCostTotal === 1 ? "is" : "are"} missing a cost — margin will be overstated and profit figures inaccurate until costs are set.
            </span>
            <button
              data-testid="set-costs-btn"
              onClick={enterSetCostMode}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF9500] text-white text-[12px] font-semibold hover:bg-[#FF9500]/90 transition-colors whitespace-nowrap"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Set costs
            </button>
            {showMissingCost ? (
              <button
                onClick={() => setShowMissingCost(false)}
                className="flex items-center gap-1 text-[#FF9500] font-medium hover:underline whitespace-nowrap"
              >
                <X className="w-3.5 h-3.5" /> Clear filter
              </button>
            ) : (
              <button
                onClick={() => setShowMissingCost(true)}
                className="text-[#FF9500] font-medium hover:underline whitespace-nowrap"
              >
                Show only →
              </button>
            )}
          </div>
        )}

        {/* Set-cost mode banner */}
        {setCostMode && (
          <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-2xl bg-[#007AFF]/8 border border-[#007AFF]/30 text-[12.5px]">
            <CheckCheck className="w-4 h-4 text-[#007AFF] flex-shrink-0" />
            <span className="flex-1 text-[#007AFF] font-medium">
              Enter costs below — Tab or Enter to advance, Escape to cancel.
            </span>
            <button
              data-testid="exit-set-cost-mode-btn"
              onClick={exitSetCostMode}
              className="flex items-center gap-1 text-[#007AFF] font-medium hover:underline whitespace-nowrap"
            >
              <X className="w-3.5 h-3.5" /> Done
            </button>
          </div>
        )}

        {/* Toolbar — hidden in set-cost mode */}
        {!setCostMode && (
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
              <Input
                data-testid="products-search"
                placeholder="Search by name or SKU..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 rounded-xl border-black/10 bg-white"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="products-sort" className="w-48 rounded-xl bg-white">
                <ArrowUpDown className="w-4 h-4 mr-2 text-[#8E8E93]" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="cogs">COGS</SelectItem>
                <SelectItem value="profit">Profit</SelectItem>
                <SelectItem value="margin">Margin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Bulk action bar */}
        {someSelected && !setCostMode && (
          <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-xl bg-[#F5F5F7] border border-black/[0.06]">
            <span className="text-[13px] font-medium text-[#3C3C43]">
              {selected.size} selected
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg text-[13px] h-8"
              onClick={openBulkEdit}
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg text-[13px] h-8 text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/5"
              onClick={() => setConfirmBulk(true)}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete selected
            </Button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-[12px] text-[#8E8E93] hover:text-[#3C3C43]"
            >
              Clear
            </button>
          </div>
        )}

        {/* Table */}
        <div className="data-table-scroll overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
          <table className="w-full text-[13px] min-w-[960px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#8E8E93]">
                {!setCostMode && (
                  <th className="py-3 pl-3 pr-2 w-8">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                      className="rounded"
                    />
                  </th>
                )}
                <th className="py-3 px-3 font-medium">Product</th>
                <th className="py-3 px-3 font-medium">SKU</th>
                <th className="py-3 px-3 font-medium">Category</th>
                <th className="py-3 px-3 font-medium text-right">Stock</th>
                <th className="py-3 px-3 font-medium text-right">Price</th>
                <th className="py-3 px-3 font-medium text-right">Revenue</th>
                <th className="py-3 px-3 font-medium text-right">COGS</th>
                <th className="py-3 px-3 font-medium text-right">Profit</th>
                <th className="py-3 px-3 font-medium text-right">Margin</th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {merged.length === 0 && (
                <tr>
                  <td colSpan={setCostMode ? 10 : 11} className="py-8 text-center text-[#8E8E93]">
                    {setCostMode ? "All costs have been set! Click Done to finish." : "No products."}
                  </td>
                </tr>
              )}
              {merged.map((r) => {
                const isSelected = r.id ? selected.has(r.id) : false;
                const isSaving = savingId === r.id;
                const draftVal = draftCosts[r.id] ?? "";

                return (
                  <tr
                    key={r.sku}
                    data-testid={`product-row-${r.sku}`}
                    className={`border-t border-black/[0.04] transition-colors ${
                      setCostMode
                        ? "bg-[#FF9500]/[0.04] hover:bg-[#FF9500]/[0.07]"
                        : isSelected
                        ? "bg-[#F0F0FF]"
                        : r.missingCost
                        ? "bg-[#FF9500]/[0.04] hover:bg-[#FF9500]/[0.07]"
                        : "hover:bg-black/[0.015]"
                    }`}
                  >
                    {!setCostMode && (
                      <td className="py-3 pl-3 pr-2">
                        {r.id && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRow(r.id)}
                            aria-label={`Select ${r.name}`}
                            className="rounded"
                          />
                        )}
                      </td>
                    )}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        {r.image_url ? (
                          <Thumb src={r.image_url} alt={r.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-black/5 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium truncate max-w-[220px]">{r.name}</div>
                          {r.variation_attributes && Object.keys(r.variation_attributes).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {Object.entries(r.variation_attributes).map(([k, v]) => (
                                <span key={k} className="inline-block text-[10px] bg-[#F1E5DF] text-[#A9684E] px-1.5 py-0.5 rounded-md font-medium leading-none">
                                  {v}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono text-[12px] text-[#3C3C43]/80">{r.sku}</td>
                    <td className="py-3 px-3 text-[#8E8E93]">{r.category || "—"}</td>
                    <td className="py-3 px-3 text-right">
                      <span className={`font-mono ${r.stock <= (r.reorder_point || 10) ? "text-[#FF3B30] font-semibold" : ""}`}>
                        {r.stock ?? "—"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right font-mono font-semibold">{r.price > 0 ? fmtMoney(r.price) : <span className="text-[#8E8E93]">—</span>}</td>
                    <td className="py-3 px-3 text-right font-mono font-semibold">{fmtMoney(r.revenue)}</td>
                    <td className="py-3 px-3 text-right font-mono text-[#FF9500]">
                      {setCostMode && r.missingCost ? (
                        <div className="flex justify-end">
                          <Input
                            data-cost-input={r.id}
                            data-testid={`cost-input-${r.sku}`}
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={draftVal}
                            disabled={isSaving}
                            onChange={(e) =>
                              setDraftCosts((prev) => ({ ...prev, [r.id]: e.target.value }))
                            }
                            onKeyDown={(e) => handleCostKeyDown(e, r)}
                            onBlur={() => handleCostBlur(r)}
                            className="w-28 h-8 text-right font-mono rounded-lg border-[#FF9500]/50 focus:border-[#FF9500] bg-white text-[#3C3C43] text-[13px]"
                            autoFocus={merged[0]?.id === r.id}
                          />
                        </div>
                      ) : r.missingCost ? (
                        <span className="inline-flex items-center gap-1 justify-end">
                          <AlertTriangle className="w-3.5 h-3.5 text-[#FF9500]" />
                          <span className="text-[11px] font-semibold text-[#FF9500]">No cost</span>
                        </span>
                      ) : (
                        fmtMoney(r.cogs)
                      )}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-[#34C759]">{fmtMoney(r.profit)}</td>
                    <td className="py-3 px-3 text-right font-mono">{r.price > 0 && r.margin != null ? `${r.margin}%` : "—"}</td>
                    <td className="py-3 px-3 text-right">
                      {r.id && !setCostMode && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            data-testid={`product-edit-${r.sku}`}
                            onClick={() => openEdit(r)}
                            className="p-1.5 text-[#8E8E93] hover:text-[#007AFF] transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`product-delete-${r.sku}`}
                            onClick={() => setConfirmTarget(r)}
                            className="p-1.5 text-[#8E8E93] hover:text-[#FF3B30] transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Single delete confirmation */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmTarget?.name}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { doDelete(confirmTarget); setConfirmTarget(null); }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} product{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              These products will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { doBulkDelete(); setConfirmBulk(false); }}
            >
              Delete {selected.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProductFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={() => { loadProducts(); setSelected(new Set()); }}
      />
    </div>
  );
}
